import Typography from '@mui/material/Typography';
import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { secondsToHMS } from 'shared/components/charts';

import {
  DonutChartWrapper,
  DonutLegendBox,
  DonutLegendDot,
  DonutLegendLeft,
  DonutLegendRow,
  donutCenterLabelSvgStyle,
  donutCenterTimeSvgStyle,
  donutLegendNameSx,
  donutLegendPctSx,
  donutTooltipDotSx,
  donutTooltipLabelSx,
  donutTooltipPctSx,
  donutTooltipSx,
  donutTooltipTimeSx,
} from '../../reports.styles';
import type { DonutSlice } from '../../types/reports.types';

// ── Tooltip ───────────────────────────────────────────────────────────────────

interface TooltipEntry extends DonutSlice {
  pct: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: TooltipEntry }>;
}

const DonutTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Typography component="div" sx={donutTooltipSx}>
      <Typography component="span" sx={{ ...donutTooltipDotSx, bgcolor: d.color }} />
      <Typography component="span" sx={donutTooltipLabelSx}>{d.label}</Typography>
      <Typography component="span" sx={donutTooltipTimeSx}>
        {secondsToHMS(d.totalSecs)}
      </Typography>
      <Typography component="span" sx={donutTooltipPctSx}>{d.pct}</Typography>
    </Typography>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  slices: DonutSlice[];
  totalSecs: number;
}

const SummaryDonutChart: React.FC<Props> = ({ slices, totalSecs }) => {
  if (!slices.length) return null;

  const data = slices.map((s) => ({
    ...s,
    value: s.totalSecs,
    pct: totalSecs > 0 ? `${((s.totalSecs / totalSecs) * 100).toFixed(2)}%` : '0%',
  }));

  return (
    <DonutChartWrapper>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={1}
            strokeWidth={0}
          >
            {data.map((entry) => (
              <Cell key={entry.id} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<DonutTooltip />} />
          <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" style={donutCenterTimeSvgStyle}>
            {secondsToHMS(totalSecs)}
          </text>
          <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle" style={donutCenterLabelSvgStyle}>
            Total
          </text>
        </PieChart>
      </ResponsiveContainer>

      {/* Legend — show 8 rows, scroll beyond */}
      <style>{`.donut-legend::-webkit-scrollbar{display:none}`}</style>
      <DonutLegendBox className="donut-legend">
        {data.map((entry) => (
          <DonutLegendRow key={entry.id}>
            <DonutLegendLeft>
              <DonutLegendDot sx={{ bgcolor: entry.color }} />
              <Typography sx={donutLegendNameSx}>
                {entry.label}
              </Typography>
            </DonutLegendLeft>
            <Typography sx={donutLegendPctSx}>
              {entry.pct}
            </Typography>
          </DonutLegendRow>
        ))}
      </DonutLegendBox>
    </DonutChartWrapper>
  );
};

export default SummaryDonutChart;
